// src/components/common/DownloadButtons.jsx
import React from "react";
import { FileDown, Image as ImageIcon, AlertCircle } from "lucide-react";

function normalize(str) {
  return (str ?? "")
    .replace(/["']/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
}

export function isAllowedLicense(licenseType) {
  const t = normalize(licenseType);
  if (!t) return false;
  if (t === "NO EXPIRE") return true;
  if (t.startsWith("PRO")) return true;
  if (t.startsWith("ENTERPRISE")) return true;
  if (t.startsWith("ENTERPRICE")) return true; // typo-safe
  return false;
}

const upgradeMsg =
  "Ta funkcja jest dostępna tylko w planach: PRO, ENTERPRISE. Uaktualnij wersję, aby pobrać PDF/PNG.";

export const DownloadButtons = ({
  licenseType,
  onDownloadPDF,
  onDownloadPNG,
  disabled = false,
}) => {
  const allowed = isAllowedLicense(licenseType);
  const isDisabled = disabled || !allowed;

  const handleBlocked = () => alert(upgradeMsg);

  // Container highlights when blocked
  const wrapperStyle = allowed
    ? { display: "flex", gap: "10px", alignItems: "center" }
    : {
        display: "flex",
        gap: "10px",
        alignItems: "center",
        padding: "6px 10px",
        borderRadius: "6px",
        border: "1px dashed #dc2626",         // red-600 dashed
        background: "rgba(220, 38, 38, 0.06)", // light red wash
      };

  // Inline button styles (your original look)
  const pdfBtnStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 16px",
    backgroundColor: allowed && !disabled ? "#87CEEB" : "#b9dff0",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: allowed && !disabled ? "pointer" : "not-allowed",
    fontSize: "0.9rem",
    transition: "all 0.3s",
    opacity: disabled ? 0.7 : 1,
  };

  const pngBtnStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 16px",
    backgroundColor: allowed && !disabled ? "#4CAF50" : "#a7d3a9",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: allowed && !disabled ? "pointer" : "not-allowed",
    fontSize: "0.9rem",
    transition: "all 0.3s",
    opacity: disabled ? 0.7 : 1,
  };

  // Hover only when enabled
  const pdfHoverIn  = (e) => { if (allowed && !disabled) e.currentTarget.style.backgroundColor = "#6cb2d8"; };
  const pdfHoverOut = (e) => { if (allowed && !disabled) e.currentTarget.style.backgroundColor = "#87CEEB"; };
  const pngHoverIn  = (e) => { if (allowed && !disabled) e.currentTarget.style.backgroundColor = "#3e8e41"; };
  const pngHoverOut = (e) => { if (allowed && !disabled) e.currentTarget.style.backgroundColor = "#4CAF50"; };

  return (
    <div style={wrapperStyle}>
      {!allowed && (
        // subtle red icon to reinforce disabled state (no text shown)
        <span title={upgradeMsg} aria-hidden="true" style={{ display: "flex", color: "#dc2626" }}>
          <AlertCircle size={18} />
        </span>
      )}

      <button
        type="button"
        style={pdfBtnStyle}
        onClick={allowed ? onDownloadPDF : handleBlocked}
        disabled={isDisabled}
        title={allowed ? "Pobierz PDF" : upgradeMsg}
        aria-disabled={isDisabled}
        onMouseOver={pdfHoverIn}
        onMouseOut={pdfHoverOut}
      >
        <FileDown size={18} />
        PDF
      </button>

      <button
        type="button"
        style={pngBtnStyle}
        onClick={allowed ? onDownloadPNG : handleBlocked}
        disabled={isDisabled}
        title={allowed ? "Pobierz PNG" : upgradeMsg}
        aria-disabled={isDisabled}
        onMouseOver={pngHoverIn}
        onMouseOut={pngHoverOut}
      >
        <ImageIcon size={18} />
        PNG
      </button>
    </div>
  );
};
